import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  ListSubheader,
  MenuItem,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import toast from 'react-hot-toast';
import {
  fetchFtpConfiguration,
  fetchReturnFileFields,
  fetchReturnFileSettings,
  previewReturnFile,
  updateReturnFileSettings,
} from '../../../services/api';
import useLoading from '../../../hooks/useLoading';
import { parseEmailList } from '../emailConfig/EmailConfig';

// The roster return file's settings are their own configuration row (RRFC), in snake_case as stored. An account that
// still has the older Internal roster settings (INBD roster_csv) gets those in this layout, marked from_legacy, until it
// saves here; they give the same file. toApi leaves from_legacy out, so saving here moves the account onto these settings.

const KINDS = [
  { key: 'new_provider', label: 'New provider', from: 'Addition sheets: every location the provider is sent at' },
  { key: 'new_location', label: 'New location', from: 'Update sheets: a site or plan participation added this cycle' },
  { key: 'removed_location', label: 'Removed location', from: 'Update sheets: a site removed this cycle, with its end date' },
  { key: 'terminated', label: 'Terminated provider', from: 'Terminate sheets: every location, with the termination date' },
];

const SOURCES = [
  { value: 'npi', label: 'NPI' },
  { value: 'provider_name', label: 'Provider name' },
  { value: 'provider_first_name', label: 'Provider first name' },
  { value: 'provider_last_name', label: 'Provider last name' },
  { value: 'hfn_id', label: 'HFN ID' },
  { value: 'provider_field', label: 'Other provider field…', field: 'Field name' },
  { value: 'location_id', label: 'Location ID' },
  { value: 'address_field', label: 'Other location field…', field: 'Field name' },
  { value: 'participation_plan_code', label: 'Participation plan code' },
  { value: 'participation_plan_name', label: 'Participation plan name' },
  { value: 'plan_name', label: 'Roster plan name' },
  { value: 'plan_code', label: 'Roster plan code' },
  { value: 'plan_field', label: 'Other participation field…', field: 'Field name' },
  { value: 'change_type', label: 'Change type' },
  { value: 'roster_change', label: 'Change type, with site changes as worded on the roster' },
  { value: 'sent_date', label: 'Sent date', date: true },
  { value: 'start_date', label: 'Participation start date', date: true },
  { value: 'end_date', label: 'Participation end date', date: true },
  { value: 'term_date', label: 'Termination date', date: true },
  { value: 'roster_id', label: 'Roster ID' },
  { value: 'text', label: 'Fixed text…', field: 'Text' },
  { value: 'blank', label: 'Blank' },
];
const sourceOf = (value) => SOURCES.find((s) => s.value === value) || SOURCES[SOURCES.length - 1];
const FIELD_SOURCES = ['provider_field', 'address_field', 'plan_field'];

// The "Contains" list, grouped. Each record group also lists the fields found on the account's records (fieldsKey),
// then "Other … field" for one not listed.
const GROUPS = [
  { label: 'Provider', items: ['npi', 'provider_name', 'provider_first_name', 'provider_last_name', 'hfn_id'], fieldSource: 'provider_field', fieldsKey: 'provider' },
  { label: 'Location', items: ['location_id'], fieldSource: 'address_field', fieldsKey: 'address' },
  { label: 'Plan', items: ['participation_plan_code', 'participation_plan_name', 'plan_name', 'plan_code'], fieldSource: 'plan_field', fieldsKey: 'participation' },
  { label: 'Change', items: ['change_type', 'roster_change'] },
  { label: 'Date', items: ['sent_date', 'start_date', 'end_date', 'term_date'] },
  { label: 'Other', items: ['roster_id', 'text', 'blank'] },
];
const OTHER_FIELD = '*';

/** address_group_id → "Address group id", HfnId → "Hfn id". */
const humanize = (key) => {
  const words = key.replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/[_\-.]+/g, ' ').trim().toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/** The "Contains" choices: [{ value, label, display, group, subheader }] in list order. */
const buildChoices = (fields) => GROUPS.flatMap((g) => [
  { subheader: g.label },
  ...g.items.map((v) => ({ value: v, label: sourceOf(v).label, display: `${g.label}: ${sourceOf(v).label}` })),
  ...(g.fieldSource ? (fields?.[g.fieldsKey] || []).map((key) => ({
    value: `${g.fieldSource}:${key}`, label: humanize(key), display: `${g.label}: ${humanize(key)}`,
  })) : []),
  ...(g.fieldSource ? [{ value: `${g.fieldSource}:${OTHER_FIELD}`, label: sourceOf(g.fieldSource).label, display: `${g.label}: other field` }] : []),
]);

/** The choice a column is: a listed field when its field is one the account has, else that record's "Other … field". */
const choiceOf = (column, fields) => {
  const group = GROUPS.find((g) => g.fieldSource === column.source);
  if (!group) return column.source;
  return (fields?.[group.fieldsKey] || []).includes(column.field) ? `${column.source}:${column.field}` : `${column.source}:${OTHER_FIELD}`;
};

// How a date is written (.NET format, as stored), shown as MM/DD/YYYY and so on.
const DATE_FORMATS = ['MM/dd/yyyy', 'M/d/yyyy', 'yyyy-MM-dd', 'MM-dd-yyyy', 'dd/MM/yyyy', 'yyyyMMdd'];
const formatLabel = (format) => format.toUpperCase();
// A field is offered a format when the file's first value for it reads as a date.
const LOOKS_LIKE_DATE = /^(\d{1,2}[/-]\d{1,2}[/-]\d{2,4}|\d{4}-\d{1,2}-\d{1,2})/;

const PLACEHOLDERS = '{roster_id}, {plan}, {plan_code}, {cycle}, {sent_date} or {sent_date:MMddyyyy}';

const emptySettings = {
  enabled: false,
  roster_id_prefix: '',
  time_zone: 'America/New_York',
  changes: {
    new_provider: { include: true, text: 'Add Provider' },
    new_location: { include: true, text: 'Add Location' },
    removed_location: { include: true, text: 'Remove Location' },
    terminated: { include: true, text: 'Terminated Provider' },
  },
  columns: [
    { header: 'NPI', source: 'npi' },
    { header: 'Location ID', source: 'location_id' },
    { header: 'Plan', source: 'plan_name' },
    { header: 'Type', source: 'change_type' },
    { header: 'Sent Date', source: 'sent_date', format: 'MM/dd/yyyy' },
  ],
  file_name: '{plan}_return_{sent_date:MMddyyyy}.csv',
  delimiter: 'comma',
  header_row: true,
  title: 'Roster return file',
  email: false,
  email_to: [],
  email_cc: [],
  sftp: 'off',
};

let nextKey = 1;
const withKey = (column) => ({ ...column, key: nextKey++ });

/** The form's view of the stored settings: every field present, addresses as comma text, each column keyed. */
const fromApi = (data) => {
  const s = { ...emptySettings, ...Object.fromEntries(Object.entries(data || {}).filter(([, v]) => v !== null && v !== undefined)) };
  const changes = Object.fromEntries(KINDS.map(({ key }) => {
    const change = { ...emptySettings.changes[key], ...(s.changes?.[key] || {}) };
    return [key, { include: !!change.include, text: change.text || '' }];
  }));
  // Nothing set up yet (no columns): start from the default columns.
  const columns = data?.columns?.length ? data.columns : emptySettings.columns;
  return {
    ...s,
    changes,
    roster_id_prefix: s.roster_id_prefix || '',
    columns: columns.map((c) => withKey({ header: c.header || '', source: c.source || 'blank', field: c.field || '', format: c.format || '' })),
    emailTo: (s.email_to || []).join(', '),
    emailCc: (s.email_cc || []).join(', '),
  };
};

/** The settings as the API takes them. */
const toApi = (form) => ({
  enabled: form.enabled,
  roster_id_prefix: form.roster_id_prefix.trim().toUpperCase() || null,
  time_zone: form.time_zone,
  changes: form.changes,
  columns: form.columns.map((c) => ({
    header: c.header.trim(),
    source: c.source,
    field: (sourceOf(c.source).field ? c.field.trim() : '') || null,
    format: (sourceOf(c.source).date || FIELD_SOURCES.includes(c.source) ? c.format.trim() : '') || null,
  })),
  file_name: form.file_name.trim(),
  delimiter: form.delimiter,
  header_row: form.header_row,
  title: form.title.trim() || 'Roster return file',
  email: form.email,
  email_to: parseEmailList(form.emailTo),
  email_cc: parseEmailList(form.emailCc),
  sftp: form.sftp === 'version_folder' ? 'version_folder' : 'off',
});

const EMAIL_PATTERN = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

function validate(form) {
  if (!form.enabled) return [];
  const errors = [];
  if (form.roster_id_prefix.trim() && !/^[A-Za-z0-9]+$/.test(form.roster_id_prefix.trim())) {
    errors.push('The Roster ID prefix must be letters and digits');
  }
  if (!KINDS.some(({ key }) => form.changes[key].include)) errors.push('Choose at least one kind of change to list');
  KINDS.forEach(({ key, label }) => {
    if (form.changes[key].include && !form.changes[key].text.trim()) errors.push(`Say how a ${label.toLowerCase()} is written`);
  });
  if (!form.columns.length) errors.push('Add at least one column');
  form.columns.forEach((c, i) => {
    const source = sourceOf(c.source);
    if (form.header_row && !c.header.trim()) errors.push(`Column ${i + 1} needs a header`);
    if (FIELD_SOURCES.includes(c.source) && !c.field.trim()) errors.push(`Column ${i + 1} (${c.header || source.label}): enter the field name`);
  });
  if (!form.file_name.trim()) errors.push('The file name is required');
  if (form.email) {
    const to = parseEmailList(form.emailTo);
    if (!to.length) errors.push('Add at least one address to email the file to');
    const bad = [...to, ...parseEmailList(form.emailCc)].filter((a) => !EMAIL_PATTERN.test(a));
    if (bad.length) errors.push(`Not a valid email address: ${bad.join(', ')}`);
  }
  return errors;
}

const cardHeaderSx = {
  backgroundColor: '#f5f5f5',
  '& .MuiCardHeader-title': { fontSize: '1.1rem', fontWeight: 600 },
};
const hintSx = { fontSize: 12, color: '#666' };

// #, column name, contains, format, buttons: the same on every row and on the header row.
const COLUMN_GRID = { display: 'grid', gridTemplateColumns: '24px minmax(130px, 1fr) minmax(200px, 1.4fr) minmax(130px, 0.7fr) 108px', columnGap: 1.5, alignItems: 'start' };

function ColumnRow({ column, index, count, choices, fields, example, onChange, onMove, onRemove, disabled }) {
  const source = sourceOf(column.source);
  const isField = FIELD_SOURCES.includes(column.source);
  const choice = choiceOf(column, fields);
  const isOther = choice.endsWith(`:${OTHER_FIELD}`);
  // A date is written in the chosen way; a field is too once it holds a date (Columbia's address end_date).
  const showFormat = source.date || (isField && (!!column.format || LOOKS_LIKE_DATE.test(example || '')));
  const formats = column.format && !DATE_FORMATS.includes(column.format) ? [...DATE_FORMATS, column.format] : DATE_FORMATS;
  const displayOf = Object.fromEntries(choices.filter((c) => c.value).map((c) => [c.value, c.display]));

  const choose = (value) => {
    const [picked, field] = value.split(':');
    if (field !== undefined) {
      onChange({ source: picked, field: field === OTHER_FIELD ? '' : field, format: picked === column.source ? column.format : '' });
      return;
    }
    onChange({
      source: picked,
      field: picked === 'text' ? column.field : '',
      format: sourceOf(picked).date ? column.format || 'MM/dd/yyyy' : '',
    });
  };

  return (
    <Box sx={{ ...COLUMN_GRID, py: 1, borderBottom: '1px solid #eee' }}>
      <Typography sx={{ pt: 1, color: '#888', fontSize: 13 }}>{index + 1}</Typography>
      <TextField
        value={column.header}
        onChange={(e) => onChange({ header: e.target.value })}
        disabled={disabled}
        size="small"
        fullWidth
        inputProps={{ 'aria-label': `Column ${index + 1} name` }}
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
        <TextField
          select
          value={choice}
          onChange={(e) => choose(e.target.value)}
          disabled={disabled}
          size="small"
          fullWidth
          SelectProps={{ renderValue: (v) => displayOf[v] || v, MenuProps: { PaperProps: { sx: { maxHeight: 420 } } } }}
          inputProps={{ 'aria-label': `Column ${index + 1} contains` }}
        >
          {choices.map((c) => (c.subheader
            ? <ListSubheader key={`h-${c.subheader}`} disableSticky sx={{ lineHeight: '32px', fontWeight: 600 }}>{c.subheader}</ListSubheader>
            : <MenuItem key={c.value} value={c.value} sx={{ pl: 3 }}>{c.label}</MenuItem>))}
        </TextField>
        {(isOther || column.source === 'text') && (
          <TextField
            label={source.field}
            placeholder={column.source === 'text' ? 'Written on every row' : 'As named on the record'}
            value={column.field}
            onChange={(e) => onChange({ field: e.target.value })}
            disabled={disabled}
            size="small"
            fullWidth
          />
        )}
      </Box>
      {showFormat ? (
        <TextField
          select
          value={column.format || ''}
          onChange={(e) => onChange({ format: e.target.value })}
          disabled={disabled}
          size="small"
          fullWidth
          SelectProps={{ displayEmpty: true }}
          inputProps={{ 'aria-label': `Column ${index + 1} format` }}
        >
          {isField && <MenuItem value="">As stored</MenuItem>}
          {formats.map((f) => <MenuItem key={f} value={f}>{formatLabel(f)}</MenuItem>)}
        </TextField>
      ) : <Box />}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="Move up"><span>
          <IconButton size="small" onClick={() => onMove(-1)} disabled={disabled || index === 0}><ArrowUpwardIcon fontSize="small" /></IconButton>
        </span></Tooltip>
        <Tooltip title="Move down"><span>
          <IconButton size="small" onClick={() => onMove(1)} disabled={disabled || index === count - 1}><ArrowDownwardIcon fontSize="small" /></IconButton>
        </span></Tooltip>
        <Tooltip title="Remove"><span>
          <IconButton size="small" onClick={onRemove} disabled={disabled}><DeleteOutlineIcon fontSize="small" /></IconButton>
        </span></Tooltip>
      </Box>
    </Box>
  );
}

/**
 * Admin Config > Roster Return File: the file the partner receives for every payer roster that is approved and sent --
 * which changes it lists and in what words, its columns, and where it goes. Never sent to the payer.
 */
export default function ReturnFileConfig() {
  const [form, setForm] = useState(() => fromApi(null));
  const [loadState, setLoadState] = useState('loading');
  const [preview, setPreview] = useState(null);
  const [previewState, setPreviewState] = useState('idle');
  // The version folder is written through the FTP inbound connection, so the SFTP choice is only offered when FTP inbound
  // is on. Shown when the FTP settings can't be read, rather than hiding a stored choice.
  const [sftpAvailable, setSftpAvailable] = useState(true);
  // The field names on the account's records, for the "Contains" list; none when they can't be read ("Other" still works).
  const [fields, setFields] = useState(null);
  const choices = useMemo(() => buildChoices(fields), [fields]);
  const { withLoading } = useLoading();
  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const disabled = !form.enabled;

  // Only the latest preview asked for is shown: an earlier one that answers late is dropped.
  const previewRequest = useRef(0);
  const runPreview = useCallback(async (current) => {
    const request = ++previewRequest.current;
    setPreviewState('loading');
    try {
      const res = await previewReturnFile(toApi(current));
      if (request !== previewRequest.current) return;
      setPreview(res?.response_data || null);
      setPreviewState('ready');
    } catch (error) {
      if (request !== previewRequest.current) return;
      console.error('Error previewing the roster return file:', error);
      setPreviewState('error');
    }
  }, []);

  const load = useCallback(async () => {
    setLoadState('loading');
    await withLoading(async () => {
      try {
        const [res, ftp, fieldsRes] = await Promise.all([
          fetchReturnFileSettings(),
          fetchFtpConfiguration().catch(() => null),
          fetchReturnFileFields().catch(() => null),
        ]);
        setSftpAvailable(!ftp || !!ftp.response_data?.inbound?.enabled);
        setFields(fieldsRes?.response_data || null);
        setForm(fromApi(res?.response_data));
        setLoadState('ready');
      } catch (error) {
        console.error('Error fetching the roster return file settings:', error);
        setLoadState('error');
      }
    });
  }, [withLoading]);

  useEffect(() => {
    load();
  }, [load]);

  // The preview (and each column's example) follows the settings on screen, a moment after the last edit.
  const previewKey = loadState === 'ready' ? JSON.stringify(toApi(form)) : null;
  useEffect(() => {
    if (!previewKey) return undefined;
    const timer = setTimeout(() => runPreview(form), 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, runPreview]);

  const handleSave = async () => {
    if (loadState !== 'ready') return;
    const errors = validate(form);
    if (errors.length) {
      toast.error(errors.join('\n'));
      return;
    }
    await withLoading(async () => {
      try {
        const res = await updateReturnFileSettings(toApi(form));
        setForm(fromApi(res?.response_data));
        toast.success(res?.user_message || 'Roster return file settings saved');
      } catch (error) {
        // 4xx/5xx messages are already shown by the API client interceptor.
        console.error('Error saving the roster return file settings:', error);
      }
    });
  };

  const setChange = (key, patch) => setForm((prev) => ({ ...prev, changes: { ...prev.changes, [key]: { ...prev.changes[key], ...patch } } }));
  const setColumn = (index, patch) => setForm((prev) => ({ ...prev, columns: prev.columns.map((c, i) => (i === index ? { ...c, ...patch } : c)) }));
  const moveColumn = (index, by) => setForm((prev) => {
    const columns = [...prev.columns];
    const [moved] = columns.splice(index, 1);
    columns.splice(index + by, 0, moved);
    return { ...prev, columns };
  });
  const removeColumn = (index) => setForm((prev) => ({ ...prev, columns: prev.columns.filter((_, i) => i !== index) }));
  const addColumn = () => setForm((prev) => ({ ...prev, columns: [...prev.columns, withKey({ header: '', source: 'blank', field: '', format: '' })] }));

  const prefix = form.roster_id_prefix.trim().toUpperCase();
  const rosterIdExample = `${prefix ? `${prefix}-` : ''}AETNA-10222026`;
  const badTo = useMemo(() => parseEmailList(form.emailTo).filter((a) => !EMAIL_PATTERN.test(a)), [form.emailTo]);
  const badCc = useMemo(() => parseEmailList(form.emailCc).filter((a) => !EMAIL_PATTERN.test(a)), [form.emailCc]);

  return (
    <Box p={3} sx={{ height: '100%', overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: '#333' }}>
          Roster Return File
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" onClick={load} disabled={loadState === 'loading'}>Discard changes</Button>
          <Button variant="contained" onClick={handleSave} disabled={loadState !== 'ready'}>Save</Button>
        </Box>
      </Box>

      {loadState === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          The roster return file settings could not be loaded, so they cannot be changed right now.
        </Alert>
      )}

      {loadState === 'ready' && (
      <Grid container spacing={3} direction="column">
        <Grid item xs={12}>
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardHeader
              title="Return file"
              subheader="When a payer roster is approved and sent, write a file for the partner of the provider locations it added and ended. Every plan, one file per roster. Never sent to the payer."
              action={
                <FormControlLabel
                  control={<Switch checked={form.enabled} onChange={(e) => update({ enabled: e.target.checked })} />}
                  label="Enabled"
                />
              }
              sx={cardHeaderSx}
            />
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <TextField
                    label="Roster ID prefix"
                    placeholder="Optional, e.g. NDEL"
                    helperText={`Roster IDs look like ${rosterIdExample}`}
                    value={form.roster_id_prefix}
                    onChange={(e) => update({ roster_id_prefix: e.target.value })}
                    disabled={disabled}
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardHeader title="Rows and wording" subheader="Which changes the file lists, and how its change type column says each" sx={cardHeaderSx} />
            <CardContent sx={{ p: 3 }}>
              {KINDS.map(({ key, label, from }) => {
                const change = form.changes[key];
                const off = disabled || !change.include;
                return (
                  <Box key={key} sx={{ py: 1, borderBottom: '1px solid #eee' }}>
                    {/* Wrapping only on narrow screens: a wrapping row in the page's column layout is measured as if it
                        wrapped, which left a gap under this card. */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: { xs: 'wrap', lg: 'nowrap' }, gap: 2 }}>
                      <FormControlLabel
                        sx={{ width: 220, flexShrink: 0 }}
                        control={<Checkbox checked={change.include} onChange={(e) => setChange(key, { include: e.target.checked })} disabled={disabled} />}
                        label={label}
                      />
                      <TextField
                        label="Written as"
                        value={change.text}
                        onChange={(e) => setChange(key, { text: e.target.value })}
                        disabled={off}
                        size="small"
                        sx={{ width: 240, flexShrink: 0 }}
                      />
                      <Typography sx={{ ...hintSx, flex: '1 1 240px' }}>{from}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardHeader title="Columns" subheader="In the order they appear in the file" sx={cardHeaderSx} />
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ ...COLUMN_GRID, pb: 1, borderBottom: '1px solid #eee' }}>
                {['#', 'Column name in the file', 'Contains', 'Format', ''].map((title) => (
                  <Typography key={title || 'actions'} sx={{ fontSize: 12, color: '#666' }}>{title}</Typography>
                ))}
              </Box>
              {form.columns.map((column, index) => (
                <ColumnRow
                  key={column.key}
                  column={column}
                  index={index}
                  count={form.columns.length}
                  choices={choices}
                  fields={fields}
                  example={preview?.examples?.[index]}
                  onChange={(patch) => setColumn(index, patch)}
                  onMove={(by) => moveColumn(index, by)}
                  onRemove={() => removeColumn(index)}
                  disabled={disabled}
                />
              ))}
              <Button startIcon={<AddIcon />} onClick={addColumn} disabled={disabled} sx={{ mt: 2 }}>Add column</Button>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardHeader title="File" sx={cardHeaderSx} />
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={6}>
                  <TextField
                    label="File name"
                    value={form.file_name}
                    onChange={(e) => update({ file_name: e.target.value })}
                    disabled={disabled}
                    helperText={`Can use ${PLACEHOLDERS}`}
                    fullWidth
                    size="small"
                    InputProps={{ sx: { fontFamily: 'monospace' } }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField select label="Delimiter" value={form.delimiter} onChange={(e) => update({ delimiter: e.target.value })} disabled={disabled} fullWidth size="small">
                    <MenuItem value="comma">Comma ,</MenuItem>
                    <MenuItem value="pipe">Pipe |</MenuItem>
                    <MenuItem value="tab">Tab</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={<Switch checked={form.header_row} onChange={(e) => update({ header_row: e.target.checked })} disabled={disabled} />}
                    label="Header row"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardHeader title="Delivery" subheader="Every file can also be downloaded on the Payers Exchange Tracker" sx={cardHeaderSx} />
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={<Switch checked={form.email} onChange={(e) => update({ email: e.target.checked })} disabled={disabled} />}
                    label="Email the file"
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    label="Email subject starts with"
                    value={form.title}
                    onChange={(e) => update({ title: e.target.value })}
                    disabled={disabled || !form.email}
                    helperText={`e.g. ${form.title || 'Roster return file'}: ${rosterIdExample} (Aetna)`}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="To"
                    value={form.emailTo}
                    onChange={(e) => update({ emailTo: e.target.value })}
                    disabled={disabled || !form.email}
                    required={form.email}
                    error={badTo.length > 0}
                    helperText={badTo.length ? `Not a valid email address: ${badTo.join(', ')}` : 'Separate multiple addresses with commas. Never the payer.'}
                    multiline
                    minRows={2}
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="CC"
                    value={form.emailCc}
                    onChange={(e) => update({ emailCc: e.target.value })}
                    disabled={disabled || !form.email}
                    error={badCc.length > 0}
                    helperText={badCc.length ? `Not a valid email address: ${badCc.join(', ')}` : 'Optional'}
                    multiline
                    minRows={2}
                    fullWidth
                    size="small"
                  />
                </Grid>
                {sftpAvailable && (
                <Grid item xs={12}>
                  <FormControlLabel
                    control={<Switch checked={form.sftp === 'version_folder'} onChange={(e) => update({ sftp: e.target.checked ? 'version_folder' : 'off' })} disabled={disabled} />}
                    label="Put the file in the SFTP version folder the roster's data came from"
                  />
                </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card elevation={3} sx={{ width: '100%' }}>
            <CardHeader
              title="Preview"
              subheader={preview ? `The latest roster sent: ${preview.planName}, ${preview.rosterId}. Settings on screen, not yet saved.` : 'The settings on screen, applied to the latest roster sent'}
              action={<Button startIcon={<RefreshIcon />} onClick={() => runPreview(form)} disabled={previewState === 'loading'}>Refresh</Button>}
              sx={cardHeaderSx}
            />
            <CardContent sx={{ p: 3 }}>
              {previewState === 'error' && <Alert severity="warning">The preview could not be made.</Alert>}
              {previewState === 'ready' && !preview && (
                <Typography sx={hintSx}>No roster has been sent with a return file yet, so there is nothing to preview.</Typography>
              )}
              {preview && (
                <>
                  <Box
                    component="pre"
                    sx={{ m: 0, p: 2, bgcolor: '#f7f7f7', borderRadius: 1, fontSize: 12, lineHeight: 1.6, overflowX: 'auto', whiteSpace: 'pre' }}
                  >
                    {preview.lines.length ? preview.lines.join('\n') : '(empty file)'}
                  </Box>
                  <Typography sx={{ ...hintSx, mt: 1 }}>
                    {preview.fileName} · {preview.rows} row{preview.rows === 1 ? '' : 's'}
                    {preview.rows > preview.lines.length - (form.header_row ? 1 : 0) ? ' (first rows shown)' : ''}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      )}
    </Box>
  );
}
